import { Component, computed, effect, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ThemeService } from './core/services/theme.service';
import { CookieConsentBannerComponent } from './shared/ui/cookie-consent-banner/cookie-consent-banner.component';
import { MascotBubbleComponent } from './shared/ui/mascot-bubble/mascot-bubble.component';
import { WindowDragRegionComponent } from './shared/ui/window-drag-region/window-drag-region.component';
import { ToastModule } from '@openng/optimus-ui/toast';
import { ButtonModule } from '@openng/optimus-ui/button';
import { UpdateService } from './core/services/update.service';
import { InactivityLogoutService } from './core/services/inactivity-logout.service';
import { SessionExpiryService } from './core/services/session-expiry.service';
import { TourService } from './core/services/tour.service';
import { FeatureFlagService } from './core/services/feature-flag.service';
import { PlatformService } from './core/services/platform.service';
import { MeetingReminderService } from './core/services/meeting-reminder.service';
import { AuthService } from './core/services/auth.service';
import { MeetingReminderOverlayComponent } from './features/meeting-reminder/components/meeting-reminder-overlay.component';
import { OnboardingWaitScreenComponent } from './features/onboarding/components/onboarding-wait-screen/onboarding-wait-screen.component';
import { OnboardingProgressNotificationComponent } from './features/onboarding/components/onboarding-progress-notification/onboarding-progress-notification.component';
import { OnboardingOrchestratorService } from './core/services/onboarding-orchestrator.service';
import { OnboardingHandoffService } from './core/services/onboarding-handoff.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    CookieConsentBannerComponent,
    MascotBubbleComponent,
    WindowDragRegionComponent,
    ToastModule,
    ButtonModule,
    MeetingReminderOverlayComponent,
    OnboardingWaitScreenComponent,
    OnboardingProgressNotificationComponent,
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private themeService = inject(ThemeService);
  public updateService = inject(UpdateService);
  private inactivityLogoutService = inject(InactivityLogoutService);
  // Eager: it watches for the end of a call that outlived its session, in every window.
  private sessionExpiryService = inject(SessionExpiryService);
  private tourService = inject(TourService);
  private featureFlagService = inject(FeatureFlagService);
  private readonly router = inject(Router);
  private readonly platform = inject(PlatformService);
  private readonly authService = inject(AuthService);
  private readonly meetingReminderService = inject(MeetingReminderService);
  protected readonly updateState = this.updateService.updateState;
  private orchestrator = inject(OnboardingOrchestratorService);
  private readonly handoff = inject(OnboardingHandoffService);

  /**
   * Said once, right after the knowledge base flies into the gear and the product opens up — but
   * never over billing. A plan that cannot reach practice is sent to "Choose your plan" by the
   * subscription guard, and congratulating someone on a workspace there is nonsense.
   */
  protected readonly handoffGreeting = computed(() => {
    if (!this.handoff.greeting()) return false;
    const url = this.currentUrl() || '';
    const isBillingFlow = url.includes('subscription-') || url.includes('/pricing') || url.includes('/checkout');
    return url.startsWith('/main') && !isBillingFlow;
  });
  protected readonly handoffGreetingMessage =
    "You're ready. Try one of the tools.<br><br>Your Knowledge Base now lives in <strong>Settings</strong>.";
  protected readonly handoffGreetingResponses = [{ id: 'got-it', label: 'Got it', primary: true }] as const;
  protected dismissHandoffGreeting(): void {
    this.handoff.dismissGreeting();
  }

  protected readonly tourVisible = computed(() => {
    const url = this.currentUrl() || '';
    const isLockedOut = url.includes('subscription-');
    return this.tourService.visible() && !this.orchestrator.isWaitScreenVisible() && !isLockedOut;
  });

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  // Screens under `/main` carry their own `app-title-bar`; the frameless PiP
  // windows draw their own chrome. Everywhere else in desktop Electron the top
  // edge would be un-draggable — and on Windows would have no caption buttons
  // at all — so we add the global strip.
  protected readonly showWindowDragRegion = computed(() => this.platform.showCustomTitleBar && !this.isMainShellRoute(this.currentUrl()));

  private isMainShellRoute(url: string): boolean {
    return (
      url === '/main' ||
      url.startsWith('/main/') ||
      url.startsWith('/main?') ||
      url.startsWith('/assistant-pip') ||
      url.startsWith('/meeting-reminder-pip')
    );
  }

  title = 'knowzilla';

  constructor() {
    this.manageReminderConnection();
  }

  private manageReminderConnection(): void {
    const remindersEnabled = this.featureFlagService.isFeatureEnabled('MeetingReminders');
    const isAuthenticated = computed(() => this.authService.currentUser() !== null);
    const shouldConnect = computed(() => remindersEnabled() && isAuthenticated());

    effect(() => {
      if (shouldConnect()) {
        this.meetingReminderService.connect();
      } else {
        this.meetingReminderService.disconnect();
      }
    });
  }

  ngOnInit() {
    this.inactivityLogoutService.start();
    this.updateService.init();
  }

  onRestart() {
    this.updateService.installUpdate();
  }
}
